import React, { useEffect } from 'react';
import Card from '../../card/Card';
import RadioComponent from '../../../fieldComponents/radioComponent/RadioComponent';
import { Box, Stack } from '@mui/material';
import './FlightData.scss';
import InputComponent from '../../../fieldComponents/inputComponent/InputComponent';
import DropdownComponent from '../../../fieldComponents/dropdownComponent/DropdownComponent';
import { datesCreator } from '../../../utils/dateHandler';
import useWeatherOverrideApi from '../../../customHooks/useWeatherOverrideApi/useWeatherOverrideApi';
import {
  AirlineType,
  ApiQueryParam,
  WeatherOverrideAirline,
} from '../../../enums/enum';
import { useAppInsightConsumer } from '../../context/AppInsightContext/AppInsightContext';
import { useAuth } from '../../context/AuthContext/AuthContext';
import {
  createErrorTelemetryProperties,
  getErrorMessage,
} from '../../../utils/appInsightUtils';
import { AxiosError } from 'axios';
import {
  appStateActions,
  useAppDispatch,
  useAppSelector,
} from '../../../store/store';

const FlightData: React.FC = () => {
  const { trackError, logError, logInformation, trackEvent } =
    useAppInsightConsumer();

  const { userData } = useAuth();

  const {
    airlineTypeData,
    reqValidity,
    weatherOverideAirlineData,
    packetReqLoading,
    flightDate,
  } = useAppSelector((state) => state.appStateReducer);

  const dispatch = useAppDispatch();

  const { isPaused, isError, isSuccess, error, data, refetch } =
    useWeatherOverrideApi(airlineTypeData);

  const handleAirlineRadioData = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // AppInsight Logging
    trackEvent(
      `${event?.target?.value} airline is selected by ${userData?.profile?.email}`,
    );

    dispatch(appStateActions.updateAirlineTypeData(event?.target?.value));
  };

  // When the WeatherOverride API gets successfull
  useEffect(() => {
    if (isSuccess) {
      // If the WeatherOverride API is successfull then reset the error state. This helps to clear any
      // previous error
      dispatch(appStateActions.updateErrorState(undefined));

      try {
        const weatherOverrideAirlineData =
          typeof data === 'string' ? data.toUpperCase().trim() : '';

        logInformation(
          `Response from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint with parameter { airline : ${airlineTypeData} } 
          came up successfully for user ${userData?.profile?.email} and xml is successfully parsed`,
          'FlightData',
          'useEffect',
        );

        if (!weatherOverideAirlineData) {
          logInformation(
            `Response from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint with parameter { airline : ${airlineTypeData} }
             have no data for user ${userData?.profile?.email}`,
            'FlightData',
            'useEffect',
          );
        }

        // Update the global state
        dispatch(
          appStateActions.updateWeatherOverideAirlineData(
            // If the data returned from Api is undefined then by default make it to NONE
            weatherOverrideAirlineData || WeatherOverrideAirline.NONE,
          ),
        );
      } catch (err) {
        logError(
          getErrorMessage(
            new Error(`Error while parsing the XML response from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint 
            for user ${userData?.profile?.email}`),
          ) as string,
          'FlightData',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(
            new Error(`Error while parsing the XML response from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint 
            for user ${userData?.profile?.email}`) as Error | AxiosError,
          ),
        );

        trackError(
          new Error(`Error while parsing the XML response from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint 
          for user ${userData?.profile?.email}`) as Error | AxiosError,
        );

        logError(
          getErrorMessage(err as Error | AxiosError) as string,
          'FlightData',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(err as Error | AxiosError),
        );

        trackError(err as Error | AxiosError);

        // Handling the error during XML Parsing
        dispatch(appStateActions.updateErrorState(err as Error));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  // If there is any error during station API call. Can be netowk disconnect or 5xx / 4xx error from the apis
  useEffect(() => {
    // If Netowrk Error
    if (isPaused) {
      // AppInsight Logging
      logError(
        getErrorMessage(
          new Error(
            `Network Error from ${ApiQueryParam.GETWEATHEROVERRIDE} endoint for ${userData?.profile?.email}`,
          ),
        ) as string,
        'FlightData',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(
          new Error(
            `Network Error from ${ApiQueryParam.GETWEATHEROVERRIDE} endoint for ${userData?.profile?.email}`,
          ),
        ),
      );
      trackError(
        new Error(
          `Network Error from ${ApiQueryParam.GETWEATHEROVERRIDE} endoint for ${userData?.profile?.email}`,
        ),
      );
      dispatch(
        appStateActions.updateErrorState(
          new Error(
            'You are not connected to any Network. Please connect to a network and try again',
          ),
        ),
      );
    }
    // If there is 5xx / 4xx response from the api
    else if (isError) {
      // AppInsight Logging
      logError(
        getErrorMessage(
          new Error(
            `Error from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint for ${userData?.profile?.email}
            with parameter { airline : ${airlineTypeData} }`,
          ),
        ) as string,
        'FlightData',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(
          new Error(
            `Error from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint for ${userData?.profile?.email}
            with parameter { airline : ${airlineTypeData} }`,
          ),
        ),
      );

      trackError(
        new Error(
          `Error from ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint for ${userData?.profile?.email} with parameter { airline : ${airlineTypeData} }`,
        ),
      );

      logError(
        getErrorMessage(error as Error | AxiosError) as string,
        'FlightData',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(error),
      );

      trackError(error);

      dispatch(appStateActions.updateErrorState(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isPaused]);

  // Once the user changes the airlineTypeDaa using the Radio button, the api needs o be refetched
  // and checked again for the weather override for that particular airline.
  useEffect(() => {
    logInformation(
      `Calling ${ApiQueryParam.GETWEATHEROVERRIDE} endpoint with parameter { airline : ${airlineTypeData} }`,
      'FlightData',
      'useEffect',
    );

    refetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airlineTypeData]);

  return (
    <Card stepNumber={3} title="Select a flight">
      <Stack
        direction={{
          md: 'column',
          lg: 'row',
        }}
        alignItems={{
          md: 'flex-start',
          lg: 'center',
        }}
        gap={{
          xs: 4,
          md: 4,
          lg: 9,
        }}
        flexWrap="wrap"
      >
        {/* Airline Radio */}
        <Stack gap={0}>
          <p>Airline</p>

          {/* If the Packet Request API status is in loading state the disable the radio group */}
          {packetReqLoading ? (
            <auro-radio-group
              data-testid="radio-group-aircraft"
              horizontal
              required
              onClick={handleAirlineRadioData}
              error={
                // When valid error should be undefined else show the error message
                reqValidity.airlineType
                  ? undefined
                  : 'Please select the type of printer'
              }
              disabled
            >
              <RadioComponent
                componentName="flight"
                label="AS"
                value="AS"
                checked={airlineTypeData === AirlineType.AS}
                // If the Airline coming from the response is AS then disable the radio button.
                // We will no allow user to use Jetstream UI rather the Backup will be used.
                disabled={
                  weatherOverideAirlineData === WeatherOverrideAirline.AS
                }
              />

              <RadioComponent
                componentName="flight"
                label="QX"
                value="QX"
                checked={airlineTypeData === AirlineType.QX}
                // If the Airline coming from the response is QX then disable the radio button.
                // We will no allow user to use Jetstream UI rather the Backup will be used.
                disabled={
                  weatherOverideAirlineData === WeatherOverrideAirline.QX
                }
              />
            </auro-radio-group>
          ) : (
            // After completion of packet Request Api and the loading state is false
            <auro-radio-group
              data-testid="radio-group-aircraft"
              horizontal
              required
              onClick={handleAirlineRadioData}
              error={
                // When valid error should be undefined else show the error message
                reqValidity.airlineType
                  ? undefined
                  : 'Please select the type of printer'
              }
            >
              <RadioComponent
                componentName="flight"
                label="AS"
                value="AS"
                checked={airlineTypeData === AirlineType.AS}
                // If the Airline coming from the response is AS then disable the radio button.
                // We will no allow user to use Jetstream UI rather the Backup will be used.
                disabled={
                  weatherOverideAirlineData === WeatherOverrideAirline.AS
                }
              />

              <RadioComponent
                componentName="flight"
                label="QX"
                value="QX"
                checked={airlineTypeData === AirlineType.QX}
                // If the Airline coming from the response is QX then disable the radio button.
                // We will no allow user to use Jetstream UI rather the Backup will be used.
                disabled={
                  weatherOverideAirlineData === WeatherOverrideAirline.QX
                }
              />
            </auro-radio-group>
          )}
        </Stack>

        {/* Flight Number and Flight Origin */}
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          gap={1}
        >
          {/* Flight Number Input field */}
          <InputComponent
            labelName="Flight Number"
            handleChange={appStateActions.handleFlightNumber}
            validity={reqValidity.flightNumber}
            disabled={packetReqLoading}
          />

          {/* Origin Input Field */}
          <InputComponent
            labelName="Origin"
            handleChange={appStateActions.handleFlightOrigin}
            validity={reqValidity.flightOrigin}
            disabled={packetReqLoading}
          />
        </Stack>

        {/* Flight Date */}
        <Box
          mt={{
            lg: '13px',
          }}
          width={{
            md: '100%',
            lg: '24%',
          }}
        >
          <DropdownComponent
            labelId="date-select-label"
            id="date-select"
            label="Flight Date"
            menuItems={datesCreator()}
            handleChange={appStateActions.handleFlightDate}
            error={!reqValidity.flightDate}
            defaultValue={datesCreator()[1].toString()}
            value={flightDate}
          />
          <i id="date-hint-text">*Flight date is based on Pacific time zone</i>
        </Box>
      </Stack>
    </Card>
  );
};

export default FlightData;
